import Dexie from "dexie";

// ── Base record shape ─────────────────────────────────
export interface DBRecord {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

// ── Factory for creating typed app databases ──────────
export function createAppDB(
  name: string,
  version: number,
  schema: Record<string, string>
): Dexie {
  const db = new Dexie(name);
  db.version(version).stores({
    meta: "key",
    ...schema,
  });
  return db;
}

// ── Generic backup helper ─────────────────────────────
export async function exportDBToJSON(db: Dexie): Promise<string> {
  const tables = db.tables;
  const snapshot: Record<string, unknown[]> = {};

  for (const table of tables) {
    snapshot[table.name] = await table.toArray();
  }

  return JSON.stringify(
    {
      __version: 1,
      __dbName: db.name,
      __exportedAt: new Date().toISOString(),
      tables: snapshot,
    },
    null,
    2
  );
}

export interface RestoreOptions {
  merge?: boolean;
}

export async function importDBFromJSON(
  db: Dexie,
  json: string,
  options: RestoreOptions = {}
): Promise<{ success: boolean; tablesRestored: string[]; errors: string[] }> {
  const { merge = false } = options;
  const errors: string[] = [];
  const tablesRestored: string[] = [];

  let payload: {
    __version: number;
    __dbName: string;
    tables: Record<string, unknown[]>;
  };

  try {
    payload = JSON.parse(json);
  } catch {
    return { success: false, tablesRestored: [], errors: ["Invalid JSON"] };
  }

  if (!payload?.tables || typeof payload.tables !== "object") {
    return { success: false, tablesRestored: [], errors: ["Missing tables key"] };
  }

  for (const [tableName, rows] of Object.entries(payload.tables)) {
    const table = db.table(tableName);
    if (!table) {
      errors.push(`Table "${tableName}" not found in current schema — skipped`);
      continue;
    }

    try {
      if (!merge) {
        await table.clear();
      }
      if (Array.isArray(rows) && rows.length > 0) {
        if (merge) {
          await table.bulkPut(rows as object[]);
        } else {
          await table.bulkAdd(rows as object[]);
        }
      }
      tablesRestored.push(tableName);
    } catch (err) {
      errors.push(`Error restoring "${tableName}": ${String(err)}`);
    }
  }

  return { success: errors.length === 0, tablesRestored, errors };
}
